// API base URLs - use proxy paths that are configured in setupProxy.js
const API_BASE_URLS = {
  manager: '/api/manager',
  data: '/api/data',
  knowledge: '/api/knowledge'
};

function authHeaders(extra = {}) {
  const token = localStorage.getItem('authToken');
  const email = localStorage.getItem('ee_assistant_email');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    // Data Access Control: forward the current user's email so the
    // data-service can annotate catalog responses with per-user
    // access_denied flags. Manager-service ignores this header.
    ...(email ? { 'X-User-Email': email } : {}),
    ...extra,
  };
}

// ── Session-expiry signal ─────────────────────────────────────────────
// A single window event ('auth:expired') is dispatched whenever any API
// response returns 401. AuthContext listens for it, clears auth state,
// and shows the SessionExpiredModal so the user can sign in again
// without losing their place.
//
// We also remember the URL the user was on when expiry was detected so
// the modal can offer a "resume here" affordance after re-auth.
let _expiredDispatched = false;
function notifySessionExpired() {
  // De-dupe: many in-flight requests can return 401 in rapid succession.
  // Only the first one fires the event; the rest are no-ops until
  // resetSessionExpiredFlag() is called (after the user re-authenticates).
  if (_expiredDispatched) return;
  _expiredDispatched = true;
  try {
    window.dispatchEvent(new CustomEvent('auth:expired', {
      detail: { resumeUrl: window.location.pathname + window.location.search },
    }));
  } catch {
    /* SSR / no window */
  }
}
export function resetSessionExpiredFlag() { _expiredDispatched = false; }

// Endpoints that should NEVER trigger the global expired modal. The
// login form itself is the obvious case — a wrong password also returns
// 401 there, but that's a normal credential error, not a session
// timeout.
const EXPIRY_BYPASS_PATTERNS = [
  /\/auth\/login(\?|$)/,
  /\/auth\/register(\?|$)/,
  /\/auth\/verify-email(\?|$)/,
  /\/auth\/resend-verification(\?|$)/,
];
function shouldNotifyOn401(url) {
  return !EXPIRY_BYPASS_PATTERNS.some((re) => re.test(url));
}

async function buildErrorFromResponse(response) {
  let detail = `HTTP ${response.status}`;
  // Structured fields extracted from the body — used by the UI to
  // render friendly alerts (e.g. PERMISSION_DENIED with a hint).
  let code = null;
  let hint = null;
  let identifier = null;
  let payload = null;
  try {
    const body = await response.json();
    payload = body;
    // FastAPI wraps HTTPException(detail=…) as { detail: … }. The
    // backend often passes a STRUCTURED object as `detail`, so unwrap
    // one level before looking for a message.
    let source = body;
    if (body && typeof body === 'object' && body.detail && typeof body.detail === 'object'
        && !Array.isArray(body.detail)) {
      source = body.detail;
    }
    if (source && typeof source === 'object') {
      code = source.error || source.code || null;
      hint = source.hint || null;
      identifier = source.identifier || null;
      const msg = source.message || source.detail || source.error;
      if (typeof msg === 'string' && msg.trim()) {
        detail = msg;
      } else if (Array.isArray(body?.detail)) {
        detail = body.detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
      } else if (typeof body?.detail === 'string') {
        detail = body.detail;
      }
    } else if (typeof body === 'string' && body.trim()) {
      detail = body;
    }
  } catch {
    /* response had no JSON body */
  }
  const err = new Error(detail);
  err.status = response.status;
  err.detail = detail;
  err.code = code;
  err.hint = hint;
  err.identifier = identifier;
  err.payload = payload;
  return err;
}

/**
 * Parse SSE (Server-Sent Events) stream
 * @param {ReadableStream} stream - The response body stream
 * @param {Function} onMessage - Callback for each message
 * @param {Function} onError - Callback for errors
 * @param {Function} onComplete - Callback when stream completes
 */
export async function parseSSEStream(stream, onMessage, onError, onComplete) {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let completeCalled = false;

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        if (onComplete && !completeCalled) {
          completeCalled = true;
          onComplete();
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);

          if (data === '[DONE]') {
            if (onComplete && !completeCalled) {
              completeCalled = true;
              onComplete();
            }
            return;
          }

          try {
            const parsed = JSON.parse(data);
            if (onMessage) onMessage(parsed);
          } catch (e) {
            console.error('Error parsing SSE data:', e);
            if (onError) onError(e);
          }
        }
      }
    }
  } catch (error) {
    console.error('Stream reading error:', error);
    if (onError) onError(error);
  }
}

/**
 * Make a POST request that returns SSE stream
 */
export async function streamRequest(service, endpoint, data, onMessage, onError, onComplete) {
  const baseUrl = API_BASE_URLS[service];
  const url = `${baseUrl}${endpoint}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      if (response.status === 401 && shouldNotifyOn401(url)) notifySessionExpired();
      throw await buildErrorFromResponse(response);
    }

    await parseSSEStream(response.body, onMessage, onError, onComplete);
  } catch (error) {
    console.error('Stream request error:', error);
    if (onError) onError(error);
  }
}

/**
 * Make a regular POST request
 */
export async function postRequest(service, endpoint, data, options = {}) {
  const baseUrl = API_BASE_URLS[service];
  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
    signal: options.signal,
  });

  if (!response.ok) {
    if (response.status === 401 && shouldNotifyOn401(url)) notifySessionExpired();
    throw await buildErrorFromResponse(response);
  }

  return await response.json();
}

/**
 * Make a GET request
 */
export async function getRequest(service, endpoint, params = {}) {
  const baseUrl = API_BASE_URLS[service];
  const queryString = new URLSearchParams(params).toString();
  const url = `${baseUrl}${endpoint}${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    if (response.status === 401 && shouldNotifyOn401(url)) notifySessionExpired();
    throw await buildErrorFromResponse(response);
  }

  return await response.json();
}

/**
 * Make a PUT request
 */
export async function putRequest(service, endpoint, data) {
  const baseUrl = API_BASE_URLS[service];
  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    if (response.status === 401 && shouldNotifyOn401(url)) notifySessionExpired();
    throw await buildErrorFromResponse(response);
  }

  return await response.json();
}

/**
 * Make a DELETE request
 */
export async function deleteRequest(service, endpoint, data) {
  const baseUrl = API_BASE_URLS[service];
  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    method: 'DELETE',
    headers: authHeaders(),
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    if (response.status === 401 && shouldNotifyOn401(url)) notifySessionExpired();
    throw await buildErrorFromResponse(response);
  }

  return await response.json();
}

export default {
  streamRequest,
  postRequest,
  getRequest,
  putRequest,
  deleteRequest,
  parseSSEStream
};
